import {ListItem, UnorderedList} from '@chakra-ui/react';
import {AxiosError} from 'axios';
import {useTranslation} from 'modules/i18n';
import React from 'react';
import {ApiError as IApiError} from '../utils/errors';
import {ErrorCard} from './forms/ErrorCard';

interface HTTPStatusErrorProps {
	error?: AxiosError<IApiError>;
	match: number | number[];
	title?: string;
	children?: any;
}

/**
 * Displays an error message if an HTTP error code occured (network layer was successfull, but the answer no).
 *
 * @param param0
 * @returns
 */
export const HTTPStatusError: React.FC<HTTPStatusErrorProps> = ({
	error,
	title,
	match,
	children
}: HTTPStatusErrorProps) => {
	const hasError = Boolean(error?.isAxiosError);
	// must be an axios error
	if (!hasError) return null;

	// must have a response
	if (!error?.response) return null;

	// must match the concerned error
	const matchStatusCode =
		match instanceof Array
			? match.includes(error.response.status)
			: error.response.status === match;
	if (!matchStatusCode) return null;

	return (
		<>
			{/* <Alert
				status='error'
				variant='left-accent'
				marginTop={5}
				marginBottom={5}
			>
				<AlertIcon />
				{title ? (
					<AlertTitle mr={2}>{title}</AlertTitle>
				) : (
					<AlertTitle mr={2}>
						{t(
							error.response?.status
								? `errors.${error.response.status}`
								: 'errors.generalError'
						)}
					</AlertTitle>
				)}

				<AlertDescription>{children}</AlertDescription>
			</Alert> */}
			<ErrorCard content={children} title={title} />
		</>
	);
};

interface ApiStatusErrorProps {
	error?: AxiosError<IApiError>;
	title?: string;
	children?: any;
}

/**
 * Displays an error message if errors concerned only the axios layer (timeout, network error.)
 */
export const ApiStatusError: React.FC<ApiStatusErrorProps> = ({
	title,
	error,
	children
}: ApiStatusErrorProps) => {
	const hasError =
		error?.isAxiosError && typeof error?.response?.status === 'undefined';
	if (!hasError) return null;

	return (
		<>
			{/* <Alert
				status='error'
				variant='left-accent'
				marginTop={5}
				marginBottom={5}
			>
				<AlertIcon />
				{title ? <AlertTitle mr={2}>{title}</AlertTitle> : null}

				<AlertDescription>{children}</AlertDescription>
			</Alert> */}
			<ErrorCard content={children} title={title} />
		</>
	);
};

interface FormStatusErrorProps {
	error?: AxiosError<IApiError>;
	children?: any;
}

/**
 * Displays an error message of http status code 400, 403 and 500.
 * Tries to understand the error output.
 *
 * @param param0
 * @returns
 */
export const FormStatusError: React.FC<FormStatusErrorProps> = ({
	error,
	children
}: FormStatusErrorProps) => {
	const {t} = useTranslation();
	const hasError = Boolean(error?.isAxiosError);
	// must be an axios error
	if (!hasError) return null;

	// must have a response
	if (!error?.response) return null;

	let detailsOfError: JSX.Element = <>{t('errors.generalError')}</>;

	// special case : an apiError (often data validation error)
	// literal is provided by django api
	if (error?.response?.data?.message === 'apiError') {
		// django validation error
		// 3 cases
		if (
			typeof error.response.data.payload !== 'undefined' &&
			error.response.data.payload !== null
		) {
			// should never occur
			if (typeof error.response.data.payload === 'string') {
				detailsOfError = <>{error.response.data.payload}</>;
			} else if (
				typeof error.response.data.payload === 'object' &&
				Array.isArray(error.response.data.payload)
			) {
				detailsOfError = (
					<UnorderedList spacing={3}>
						{error.response.data.payload.map(item => {
							if (typeof item === 'string') {
								return <ListItem key={item}>{item}</ListItem>;
							}

							if (typeof item === 'object' && Array.isArray(item)) {
								return item.map(subitem =>
									Object.keys(subitem).map(key => (
										<ListItem key={key}>{subitem[key]}</ListItem>
									))
								);
							}

							return Object.keys(item).map(key => (
								<ListItem key={key}>{item[key]}</ListItem>
							));
						})}
					</UnorderedList>
				);
			} else if (
				typeof error.response.data.payload === 'object' &&
				!Array.isArray(error.response.data.payload)
			) {
				detailsOfError = (
					<UnorderedList spacing={3}>
						{Object.keys(error.response.data.payload).map(key => (
							<ListItem key={key}>
								{error?.response?.data?.payload[key]}
							</ListItem>
						))}
					</UnorderedList>
				);
			}
		}
	} else if (
		typeof error?.response?.data?.message !== 'undefined' &&
		typeof error?.response?.data?.literal !== 'undefined' &&
		typeof error?.response?.data?.statusCode !== 'undefined'
	) {
		// business error, technical error

		detailsOfError = <>{t(`apiErrors.${error?.response?.data?.message}`)}</>;
	}

	return (
		<>
			<ApiStatusError title={t('errors.unavailableService')} error={error} />

			<HTTPStatusError error={error} match={[400, 403, 500]}>
				{detailsOfError}
			</HTTPStatusError>
		</>
	);
};
